import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import AppModal from './AppModal';
import { messageFromError } from '../utils/apiError';   // Block 233

// K2 — the recipe collection. Reads like a note (name, method, notes prose) with
// structure beneath (ingredients, tags, cook time, rating). Ingredients arrive
// pre-formatted in the user's measurement system from the backend (lib/units).

const FONT = "'Raleway', sans-serif";
const NAVY = '#1a2e3b';
const TAGS = ['beef', 'pork', 'poultry', 'fish', 'mexican', 'italian', 'chinese', 'japanese', 'thai', 'indian', 'veggie', 'dessert'];

function fmtAgo(iso) {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  if (days < 60) return 'last month';
  return `${Math.floor(days / 30)} months ago`;
}

export default function Recipes() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tag, setTag] = useState('');
  const [maxTime, setMaxTime] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // K3 — shopping proposal modal state.
  const [propRecipe, setPropRecipe] = useState(null);
  const [prop, setProp] = useState(null);
  const [pHeadcount, setPHeadcount] = useState('');
  const [pServings, setPServings] = useState('');
  const [pGuests, setPGuests] = useState([]);
  const [pGuestInput, setPGuestInput] = useState('');
  const [include, setInclude] = useState({}); // name -> bool (default = line.missing)
  const [pLoading, setPLoading] = useState(false);
  const [pAdding, setPAdding] = useState(false);
  const [pDone, setPDone] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const qs = new URLSearchParams();
      if (tag) qs.set('tag', tag);
      if (maxTime) qs.set('maxCookTime', maxTime);
      const res = await api(`/api/recipes${qs.toString() ? `?${qs}` : ''}`);
      const data = await res.json();
      setRecipes(data.recipes || []);
    } catch (err) { setError(messageFromError(err, 'Could not load your recipes.')); }
    finally { setLoading(false); }
  }, [tag, maxTime]);

  useEffect(() => { load(); }, [load]);

  async function cookedIt(r) {
    try {
      const res = await api(`/api/recipes/${r.id}/cooked`, { method: 'POST' });
      const data = await res.json();
      setRecipes(prev => prev.map(x => x.id === r.id
        ? { ...x, lastCookedAt: data.lastCookedAt, timesInLast30d: data.timesInLast30d }
        : x));
    } catch (err) { setError(messageFromError(err, 'Could not record that.')); }
  }

  async function remove(r) {
    setConfirmDelete(null);
    setRecipes(prev => prev.filter(x => x.id !== r.id));
    try { await api(`/api/recipes/${r.id}`, { method: 'DELETE' }); }
    catch { setError('Delete failed.'); load(); }
  }

  // K3 — build a shopping proposal (read-only; writes nothing until confirmed).
  const fetchProposal = useCallback(async (recipeId, body) => {
    setPLoading(true);
    try {
      const res = await api(`/api/recipes/${recipeId}/shopping-proposal`, { method: 'POST', body: JSON.stringify(body || {}) });
      const data = await res.json();
      setProp(data);
      setPHeadcount(String(data.headcount));
      setPServings(String(data.servings));
      // Default each line's inclusion to whether it is missing, but keep any
      // toggle the user already made (by name).
      setInclude(prev => {
        const next = {};
        for (const l of data.lines) next[l.name] = (l.name in prev) ? prev[l.name] : l.missing;
        return next;
      });
    } catch (err) { setError(messageFromError(err, 'Could not build the shopping list.')); }
    finally { setPLoading(false); }
  }, []);

  function openProposal(r) {
    setPropRecipe(r); setProp(null); setInclude({}); setPGuests([]); setPGuestInput(''); setPDone('');
    setPHeadcount(''); setPServings(String(r.servings || ''));
    fetchProposal(r.id, { servings: r.servings });
  }

  function recompute(next = {}) {
    const body = {
      headcount: parseInt(next.headcount ?? pHeadcount, 10) || undefined,
      servings: parseInt(next.servings ?? pServings, 10) || undefined,
      guests: next.guests ?? pGuests,
    };
    fetchProposal(propRecipe.id, body);
  }

  async function proposeToFamily(r) {
    try {
      const res = await api('/api/household-polls', { method: 'POST', body: JSON.stringify({ kind: 'recipe_approval', recipeId: r.id }) });
      setError('');
      window.alert(`Asked the family to vote on keeping "${r.name}". Open the Meal Planner to vote.`);
    } catch (err) { setError(messageFromError(err, 'Could not start that vote.')); }
  }

  async function confirmAdd() {
    const items = (prop?.lines || [])
      .filter(l => include[l.name])
      .map(l => ({ name: l.name, quantity: l.quantityText || null }));
    if (!items.length) { setPropRecipe(null); return; }
    setPAdding(true);
    try {
      const res = await api('/api/shopping/items/bulk', { method: 'POST', body: JSON.stringify({ items }) });
      const data = await res.json();
      setPDone(`Added ${data.added} item${data.added === 1 ? '' : 's'} to your shopping list.`);
      setTimeout(() => setPropRecipe(null), 1400);
    } catch (err) { setError(messageFromError(err, 'Could not add that to the shopping list.')); }
    finally { setPAdding(false); }
  }

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={tag} onChange={e => setTag(e.target.value)}
          style={{ fontFamily: FONT, fontSize: '0.88rem', padding: '0.45rem 0.6rem', border: '1px solid #dde3ea', borderRadius: 8 }}>
          <option value="">All tags</option>
          {TAGS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={maxTime} onChange={e => setMaxTime(e.target.value)}
          style={{ fontFamily: FONT, fontSize: '0.88rem', padding: '0.45rem 0.6rem', border: '1px solid #dde3ea', borderRadius: 8 }}>
          <option value="">Any time</option>
          <option value="15">Under 15 min</option>
          <option value="30">Under 30 min</option>
          <option value="45">Under 45 min</option>
          <option value="60">Under 1 hour</option>
        </select>
      </div>

      {error && <div style={{ color: '#b91c1c', fontSize: '0.82rem', marginBottom: '0.75rem' }}>{error}</div>}

      {loading ? (
        <p style={{ color: '#8a99a8', fontFamily: FONT }}>Loading recipes…</p>
      ) : recipes.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#8a99a8', fontFamily: FONT, padding: '2rem 1rem', lineHeight: 1.6 }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📖</div>
          No recipes yet. When Aidey suggests a meal you like, ask it to save the recipe and it will appear here.
        </div>
      ) : (
        recipes.map(r => {
          const isOpen = expanded === r.id;
          const oftenLately = r.timesInLast30d >= 3;
          return (
            <div key={r.id} style={{ border: '1px solid #eef2f6', borderRadius: 12, marginBottom: '0.75rem', overflow: 'hidden' }}>
              <div style={{ padding: '0.85rem 1rem', cursor: 'pointer' }} onClick={() => setExpanded(isOpen ? null : r.id)}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem', color: NAVY }}>{r.name}</span>
                  {r.cookTimeMinutes != null && <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>⏱ {r.cookTimeMinutes} min</span>}
                  {r.rating ? <span style={{ fontSize: '0.78rem', color: '#eab308' }}>{'★'.repeat(r.rating)}</span> : null}
                </div>
                {r.tags?.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
                    {r.tags.map(t => (
                      <span key={t} style={{ fontSize: '0.68rem', fontWeight: 700, color: '#334155', background: '#f1f5f9', padding: '0.1rem 0.45rem', borderRadius: 6 }}>{t}</span>
                    ))}
                  </div>
                )}
                {/* The whole point: how often lately. */}
                <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: oftenLately ? '#b45309' : '#8a99a8' }}>
                  {r.lastCookedAt
                    ? <>Last cooked {fmtAgo(r.lastCookedAt)}
                        {r.timesInLast30d > 0 && <> · <strong style={{ color: oftenLately ? '#b45309' : '#4b5563' }}>{r.timesInLast30d}×</strong> in the last 30 days
                          {oftenLately && ', maybe time for something different?'}</>}
                      </>
                    : 'Not cooked yet'}
                </div>
              </div>

              {isOpen && (
                <div style={{ padding: '0 1rem 1rem', borderTop: '1px solid #f1f5f9' }}>
                  <div style={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.4, color: '#8a99a8', margin: '0.85rem 0 0.4rem' }}>
                    Ingredients {r.servings ? <span style={{ fontWeight: 400, textTransform: 'none' }}>· serves {r.servings}</span> : null}
                  </div>
                  <ul style={{ margin: '0 0 0.75rem', paddingLeft: '1.1rem', color: NAVY, fontFamily: FONT, fontSize: '0.9rem', lineHeight: 1.7 }}>
                    {(r.ingredientLines || []).map((line, i) => <li key={i}>{line}</li>)}
                  </ul>
                  {r.method && (<>
                    <div style={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.4, color: '#8a99a8', margin: '0 0 0.4rem' }}>Method</div>
                    <p style={{ margin: '0 0 0.75rem', color: '#374151', fontFamily: FONT, fontSize: '0.9rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{r.method}</p>
                  </>)}
                  {r.notes && (
                    <p style={{ margin: '0 0 0.75rem', color: '#6b7280', fontFamily: FONT, fontSize: '0.85rem', fontStyle: 'italic', background: '#f8fafc', padding: '0.5rem 0.7rem', borderRadius: 8 }}>{r.notes}</p>
                  )}
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => cookedIt(r)}
                      style={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.85rem', padding: '0.45rem 0.9rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
                      🍽 Cooked it
                    </button>
                    <button onClick={() => openProposal(r)}
                      style={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.85rem', padding: '0.45rem 0.9rem', background: '#e4e69b', color: NAVY, border: 'none', borderRadius: 8, cursor: 'pointer' }}>
                      🛒 Add to shopping
                    </button>
                    <button onClick={() => proposeToFamily(r)}
                      style={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.85rem', padding: '0.45rem 0.9rem', background: 'none', color: NAVY, border: '1px solid #dde3ea', borderRadius: 8, cursor: 'pointer' }}>
                      🗳 Propose to family
                    </button>
                    {confirmDelete === r.id ? (
                      <>
                        <button onClick={() => remove(r)} style={{ fontFamily: FONT, fontSize: '0.85rem', padding: '0.45rem 0.8rem', background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}>Delete</button>
                        <button onClick={() => setConfirmDelete(null)} style={{ fontFamily: FONT, fontSize: '0.85rem', padding: '0.45rem 0.8rem', background: 'none', color: '#6b7280', border: '1px solid #dde3ea', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
                      </>
                    ) : (
                      <button onClick={() => setConfirmDelete(r.id)} style={{ fontFamily: FONT, fontSize: '0.85rem', padding: '0.45rem 0.8rem', background: 'none', color: '#9ca3af', border: '1px solid #eef2f6', borderRadius: 8, cursor: 'pointer' }}>Delete</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}

      {/* K3 — shopping proposal (review before anything is written) */}
      <AppModal open={!!propRecipe} onClose={() => setPropRecipe(null)} closeOnBackdrop ariaLabel="Add to shopping list" maxWidth={460}>
        {propRecipe && (
          <div style={{ padding: '1.25rem 1.35rem', fontFamily: FONT }}>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.05rem', color: NAVY, fontWeight: 800, fontFamily: "'Poppins', sans-serif" }}>Add to shopping list</h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#6b7280' }}>{propRecipe.name}, scaled to who is eating. Nothing is added until you confirm.</p>

            {/* Who is eating */}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              <label style={{ fontSize: '0.82rem', color: '#4b5563' }}>Eating
                <input type="number" min="1" value={pHeadcount}
                  onChange={e => setPHeadcount(e.target.value)}
                  onBlur={() => recompute({ headcount: pHeadcount })}
                  style={{ display: 'block', width: 70, marginTop: 3, padding: '0.35rem 0.5rem', border: '1px solid #dde3ea', borderRadius: 8, fontFamily: FONT }} />
              </label>
              <label style={{ fontSize: '0.82rem', color: '#4b5563' }}>Recipe serves
                <input type="number" min="1" value={pServings}
                  onChange={e => setPServings(e.target.value)}
                  onBlur={() => recompute({ servings: pServings })}
                  style={{ display: 'block', width: 70, marginTop: 3, padding: '0.35rem 0.5rem', border: '1px solid #dde3ea', borderRadius: 8, fontFamily: FONT }} />
              </label>
            </div>

            {/* Guests */}
            <div style={{ marginBottom: '0.85rem' }}>
              <div style={{ fontSize: '0.82rem', color: '#4b5563', marginBottom: 3 }}>Guests</div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                {pGuests.map(g => (
                  <span key={g} style={{ fontSize: '0.78rem', background: '#f1f5f9', color: '#334155', padding: '0.15rem 0.5rem', borderRadius: 999 }}>
                    {g} <button onClick={() => { const ng = pGuests.filter(x => x !== g); setPGuests(ng); recompute({ guests: ng }); }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8' }}>×</button>
                  </span>
                ))}
                <input value={pGuestInput} onChange={e => setPGuestInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && pGuestInput.trim()) { const ng = [...pGuests, pGuestInput.trim()]; setPGuests(ng); setPGuestInput(''); recompute({ guests: ng }); } }}
                  placeholder="Add a guest…"
                  style={{ flex: '1 1 100px', minWidth: 80, padding: '0.3rem 0.5rem', border: '1px solid #dde3ea', borderRadius: 8, fontFamily: FONT, fontSize: '0.82rem' }} />
              </div>
            </div>

            {/* Lines */}
            {pLoading ? (
              <p style={{ color: '#8a99a8' }}>Working it out…</p>
            ) : (
              <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #eef2f6', borderRadius: 10, padding: '0.4rem' }}>
                {(prop?.lines || []).map(l => (
                  <label key={l.name} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.5rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!include[l.name]} onChange={() => setInclude(m => ({ ...m, [l.name]: !m[l.name] }))} />
                    <span style={{ flex: 1, fontSize: '0.88rem', color: NAVY, textDecoration: include[l.name] ? 'none' : 'line-through', opacity: include[l.name] ? 1 : 0.5 }}>
                      {l.displayLine}
                    </span>
                    {l.inPantry
                      ? <span style={{ fontSize: '0.68rem', color: '#065f46', background: '#d1fae5', padding: '0.1rem 0.4rem', borderRadius: 6 }}>in pantry</span>
                      : <span style={{ fontSize: '0.68rem', color: '#b45309', background: '#fef3c7', padding: '0.1rem 0.4rem', borderRadius: 6 }}>not in pantry</span>}
                  </label>
                ))}
              </div>
            )}
            <p style={{ fontSize: '0.72rem', color: '#9ca3af', margin: '0.5rem 0 0' }}>Tick to add, untick anything you already have. "In pantry" items are unticked by default.</p>

            {pDone
              ? <p style={{ color: '#065f46', fontWeight: 700, marginTop: '0.85rem' }}>{pDone}</p>
              : (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                  <button onClick={() => setPropRecipe(null)} style={{ padding: '0.5rem 1rem', background: 'none', border: '1px solid #dde3ea', borderRadius: 8, cursor: 'pointer', color: '#6b7280', fontFamily: FONT }}>Cancel</button>
                  <button onClick={confirmAdd} disabled={pAdding}
                    style={{ padding: '0.5rem 1.1rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: pAdding ? 'default' : 'pointer', fontFamily: FONT, opacity: pAdding ? 0.6 : 1 }}>
                    {pAdding ? 'Adding…' : `Add ${(prop?.lines || []).filter(l => include[l.name]).length} to list`}
                  </button>
                </div>
              )}
          </div>
        )}
      </AppModal>
    </div>
  );
}
